import { get } from "lodash";
import {
  Modal,
  Flex,
  Spacer,
  Button,
  Small,
  Icon,
  H3,
  TabsAlt,
  Box,
} from "orcs-design-system";
import PropTypes from "prop-types";
import React, { useCallback, useMemo, useState, useEffect } from "react";
import styled from "styled-components";

import { useGlobalConfig } from "src/contexts/global/GlobalContext";
import icon from "src/config/icons";
import { GroupAttribute } from "src/custom-prop-types/groupAttribute";
import { reportError } from "src/services/errorReporting";
import { trackEvent } from "src/services/segment";
import { EVENT_TRACKING } from "src/consts/eventTracking";
import UnsplashTab from "./tabs/UnsplashTab";
import CompanyImagesTab from "./tabs/CompanyImagesTab";

import { sizing, copywriting } from "./ImagePicker.config";
import {
  getSourceTypeKey,
  createOrAddImageAttribute,
} from "./ImagePicker.util";

const StyledModal = styled(Modal)`
  #modal-container {
    padding-left: 0;
    padding-right: 0;
  }
`;

const IMAGE_SOURCE_TYPES = {
  COMPANY_IMAGES: "company_images",
  STOCK_IMAGES: "stock_images",
};

const ImagePicker = ({
  attributes,
  isVisible,
  onUpdate,
  onHideModal,
  resourceTrackingType = EVENT_TRACKING.TEAM_UPDATED,
  attributeId = "bgImg",
}) => {
  const [activeTab, setActiveTab] = useState(null);
  const [selection, setSelection] = useState();
  const [isSaving, setIsSaving] = useState(false);
  const [onSaveError, setOnSaveError] = useState();
  const globalConfig = useGlobalConfig() || {};
  const stockImagesDisabled = get(
    globalConfig,
    "config.stockImagesDisabled",
    false
  );

  const onConfirm = async () => {
    if (selection) {
      setOnSaveError(null);
      setIsSaving(true);

      try {
        const { imgUrl, altKey } = getSourceTypeKey(activeTab);
        const bgImgUrl = get(selection, imgUrl);
        const altDescription = get(selection, altKey);

        const newAttributes = createOrAddImageAttribute({
          attributes,
          bgImgUrl,
          altDescription,
          attributeId,
          subtype: activeTab,
        });
        await onUpdate(newAttributes);
        onHideModal();

        if (activeTab === IMAGE_SOURCE_TYPES.STOCK_IMAGES) {
          const unsplashDownloadEvent = `${get(
            selection,
            "links.download_location"
          )}/?client_id=${process.env.REACT_APP_UNSPLASH_ACCESS_KEY}`;

          // no need to wait for this to complete
          fetch(unsplashDownloadEvent);
        }
      } catch (mutationError) {
        reportError(mutationError);
        setOnSaveError(copywriting.FORM_SAVE_ERROR);
      }

      setIsSaving(false);

      trackEvent(resourceTrackingType, {
        unsplash_saved: activeTab === IMAGE_SOURCE_TYPES.STOCK_IMAGES,
        company_images_saved: activeTab === IMAGE_SOURCE_TYPES.COMPANY_IMAGES,
      });
    } else {
      setOnSaveError(copywriting.FORM_INVALID);
    }
  };

  const onCancel = useCallback(() => {
    setOnSaveError(null);
    onHideModal();
    trackEvent(resourceTrackingType, {
      cancelled: true,
    });
  }, [resourceTrackingType, onHideModal]);

  const onSelect = useCallback(
    (selected) => {
      setSelection(selected?.data);
      trackEvent(resourceTrackingType, {
        unsplash_selected: activeTab === IMAGE_SOURCE_TYPES.STOCK_IMAGES,
        company_images_selected:
          activeTab === IMAGE_SOURCE_TYPES.COMPANY_IMAGES,
      });
    },
    [resourceTrackingType, activeTab]
  );

  const tabs = useMemo(() => {
    return [
      {
        id: IMAGE_SOURCE_TYPES.COMPANY_IMAGES,
        label: "Company Images",
        isDisabled: false,
      },
      {
        id: IMAGE_SOURCE_TYPES.STOCK_IMAGES,
        label: "Stock Images",
        isDisabled: stockImagesDisabled,
      },
    ]
      .map((tab) => ({
        ...tab,
        onClick: () => {
          setActiveTab(tab.id);
          setSelection(null);
        },
        className: activeTab === tab.id ? "active" : "",
      }))
      .filter((tab) => !tab.isDisabled);
  }, [activeTab, stockImagesDisabled]);

  useEffect(() => {
    if (!activeTab) {
      setActiveTab(tabs[0].id);
    }
  }, [tabs, activeTab]);

  const modalHeader = <H3 px="r">{copywriting.TITLE}</H3>;

  const modalFooter = (
    <Flex flexWrap="wrap" width="100%" px="r">
      <Spacer mr="s">
        <Button
          onClick={onConfirm}
          variant="success"
          iconLeft
          disabled={isSaving || !selection}
          isLoading={isSaving}
          data-testid="cp-unsplash-save-btn"
        >
          <Icon icon={["fas", "save"]} />
          Save
        </Button>
        <Button
          variant="ghost"
          onClick={onCancel}
          iconLeft
          data-testid="cp-unsplash-cancel-btn"
        >
          <Icon icon={["fas", "times"]} />
          Cancel
        </Button>
      </Spacer>
      {onSaveError && (
        <Small color="danger" fontWeight="bold" display="block" my="s">
          <Icon icon={icon.exclamation} mr="xs" />
          {onSaveError}
        </Small>
      )}
      {activeTab === IMAGE_SOURCE_TYPES.STOCK_IMAGES && (
        <Small color="greyDark" display="block" my="s" ml="auto">
          <em>{copywriting.ATTRIBUTION_LABEL}</em>
        </Small>
      )}
    </Flex>
  );

  return (
    <StyledModal
      visible={isVisible}
      onConfirm={onConfirm}
      onClose={onCancel}
      height={sizing.MODAL_HEIGHT}
      width={sizing.MODAL_WIDTH}
      maxHeight={sizing.MODAL_MAXHEIGHT}
      maxWidth={sizing.MODAL_MAXWIDTH}
      footerContent={modalFooter}
      headerContent={modalHeader}
    >
      {tabs.length > 1 && <TabsAlt tabsList={tabs} clickable />}

      <Box p="r" height="100%">
        {activeTab === IMAGE_SOURCE_TYPES.STOCK_IMAGES && (
          <UnsplashTab onSelect={onSelect} />
        )}
        {activeTab === IMAGE_SOURCE_TYPES.COMPANY_IMAGES && (
          <CompanyImagesTab onSelect={onSelect} />
        )}
      </Box>
    </StyledModal>
  );
};

ImagePicker.propTypes = {
  attributes: PropTypes.arrayOf(GroupAttribute),
  isVisible: PropTypes.bool,
  onUpdate: PropTypes.func,
  onHideModal: PropTypes.func,
  resourceTrackingType: PropTypes.string,
  attributeId: PropTypes.string,
};

export default React.memo(ImagePicker);
